import { toast } from "react-toastify";

export const eventHandlers = {
  async handleRegularItemQuantity(
    item,
    newQuantity,
    cartItems,
    eventInfo,
    storeRequester
  ) {
    if (!eventInfo) {
      const check = await storeRequester.updateItem(item.cart_id, item.id, {
        quantity: newQuantity,
      });

      if (check?.code === "insufficient_inventory") {
        const { variant } = await storeRequester.getVariant(
          item.variant_id,
          {}
        );
        if (item.quantity > 0 && item.quantity !== variant.inventory_quantity) {
          if (variant.inventory_quantity > 0) {
            await storeRequester.updateItem(item.cart_id, item.id, {
              quantity: variant.inventory_quantity,
            });
            toast.error(
              `재고량 부족으로 인해 ${item.title}${
                item.variant?.title ? item.variant.title : ""
              }의 재고량이 변경되었습니다`,
              { autoClose: 1000, position: "bottom-center" }
            );
          } else {
            await storeRequester.removeItem(item.cart_id, item.id);
            toast.error(
              `재고량 부족으로 인해 ${item.title}${
                item.variant?.title ? item.variant.title : ""
              }이 제거되었습니다`,
              { autoClose: 1000, position: "bottom-center" }
            );
          }
        }
      }
      return;
    }

    const { N, M } = eventInfo;
    const linkedFreeItems = cartItems.filter(
      (i) => i.free && i.variant.product_id === item.variant.product_id
    );

    // 메인 아이템 먼저 업데이트
    const check = await storeRequester.updateItem(item.cart_id, item.id, {
      quantity: newQuantity,
    });
    if (check?.code === "insufficient_inventory") {
      const { variant } = await storeRequester.getVariant(item.variant_id, {});
      if (item.quantity > 0 && item.quantity !== variant.inventory_quantity) {
        if (variant.inventory_quantity > 0) {
          await storeRequester.updateItem(item.cart_id, item.id, {
            quantity: variant.inventory_quantity,
          });
          toast.error(
            `재고량 부족으로 인해 ${item.title}${
              item.variant?.title ? item.variant.title : ""
            }의 재고량이 변경되었습니다`,
            { autoClose: 1000, position: "bottom-center" }
          );
        } else {
          await storeRequester.removeItem(item.cart_id, item.id);
          toast.error(
            `재고량 부족으로 인해 ${item.title}${
              item.variant?.title ? item.variant.title : ""
            }이 제거되었습니다`,
            { autoClose: 1000, position: "bottom-center" }
          );
        }
      }
      for (const freeItem of linkedFreeItems) {
        await storeRequester.removeItem(freeItem.cart_id, freeItem.id);
      }
      return;
    }
    if (newQuantity < N) {
      // free items 순차적으로 제거
      for (const freeItem of linkedFreeItems) {
        await storeRequester.removeItem(freeItem.cart_id, freeItem.id);
      }
      return;
    }

    const maxAllowedFreeItems = Math.floor(newQuantity / N) * M;
    const totalCurrentFreeItems = linkedFreeItems.reduce(
      (sum, item) => sum + item.quantity,
      0
    );

    if (totalCurrentFreeItems > maxAllowedFreeItems) {
      // free items 순차적으로 업데이트
      for (const freeItem of linkedFreeItems) {
        const newFreeQuantity = Math.floor(
          freeItem.quantity * (maxAllowedFreeItems / totalCurrentFreeItems)
        );

        if (newFreeQuantity > 0) {
          await storeRequester.updateItem(freeItem.cart_id, freeItem.id, {
            quantity: newFreeQuantity,
          });
        } else {
          await storeRequester.removeItem(freeItem.cart_id, freeItem.id);
        }
      }
    }
  },

  async handleFreeItemQuantity(
    item,
    newQuantity,
    cartItems,
    eventInfo,
    storeRequester
  ) {
    if (!eventInfo) return null;

    // 전체 event에 속한 모든 free items 찾기
    const allEventFreeItems = cartItems.filter(
      (i) => i.free && i.metadata?.event_id === eventInfo.eventId
    );

    const otherFreeItemsQuantity = allEventFreeItems
      .filter((i) => i.id !== item.id)
      .reduce((sum, i) => sum + i.quantity, 0);

    // Event 전체 regular items 찾기
    const allEventRegularItems = cartItems.filter(
      (i) =>
        !i.free &&
        eventInfo.products.some((p) => p.id === i.variant?.product?.id)
    );

    // Event 전체 허용 가능한 최대 free items 계산
    const totalRegularQuantity = allEventRegularItems.reduce(
      (sum, i) => sum + i.quantity,
      0
    );
    const maxTotalAllowedFreeItems =
      Math.floor(totalRegularQuantity / eventInfo.N) * eventInfo.M;

    if (eventInfo.cross) {
      const maxAllowedQuantity = calculateCrossEventAllowedQuantity(
        item,
        cartItems,
        eventInfo
      );
      if (otherFreeItemsQuantity + newQuantity > maxAllowedQuantity) {
        return null;
      }
    } else {
      // Non-cross event의 경우
      const variantAllowedQuantity = calculateNonCrossEventAllowedQuantity(
        item,
        eventInfo
      );

      // variant 레벨과 전체 event 레벨 모두 체크
      if (
        otherFreeItemsQuantity + newQuantity > maxTotalAllowedFreeItems ||
        newQuantity > variantAllowedQuantity
      ) {
        return null;
      }
    }

    return storeRequester.updateItem(item.cart_id, item.id, {
      quantity: newQuantity,
    });
  },

  async adjustFreeItemsAfterDelete(
    deletedItem,
    remainingItems,
    eventInfo,
    storeRequester
  ) {
    if (!eventInfo || !remainingItems.length) return [];

    const { N, M } = eventInfo;
    const eventRegularItems = remainingItems.filter(
      (item) =>
        !item.free && item.variant.product_id === deletedItem.variant.product_id
    );

    const totalRegularQuantity = eventRegularItems.reduce(
      (sum, item) => sum + item.quantity,
      0
    );

    if (totalRegularQuantity === 0) {
      return removeGroupFreeItems(remainingItems, deletedItem, storeRequester);
    }

    return adjustGroupFreeItemsQuantity(
      remainingItems,
      deletedItem,
      eventInfo,
      totalRegularQuantity,
      storeRequester
    );
  },
};

// Helper functions
function calculateCrossEventAllowedQuantity(item, cartItems, eventInfo) {
  const groupRegularItems = cartItems.filter(
    (i) => !i.free && i.title === item.title
  );
  const groupTotalQuantity = groupRegularItems.reduce(
    (sum, i) => sum + i.quantity,
    0
  );
  return Math.floor(groupTotalQuantity / eventInfo.N) * eventInfo.M;
}

function calculateNonCrossEventAllowedQuantity(item, eventInfo) {
  const variantCalc = eventInfo.variantCalculations?.[item.variant_id];
  return variantCalc?.allowedFreeItems || 0;
}

function removeGroupFreeItems(remainingItems, deletedItem, storeRequester) {
  const relatedFreeItems = remainingItems.filter(
    (item) => item.free && item.title === deletedItem.title
  );
  return relatedFreeItems.map((freeItem) =>
    storeRequester.removeItem(freeItem.cart_id, freeItem.id)
  );
}

function adjustGroupFreeItemsQuantity(
  remainingItems,
  deletedItem,
  eventInfo,
  totalRegularQuantity,
  storeRequester
) {
  const { N, M, cross } = eventInfo;
  const maxAllowedFreeItems = Math.floor(totalRegularQuantity / N) * M;
  const currentFreeItems = remainingItems.filter(
    (item) => item.free && item.title === deletedItem.title
  );
  const currentTotalFreeItems = currentFreeItems.reduce(
    (sum, item) => sum + item.quantity,
    0
  );

  if (currentTotalFreeItems <= maxAllowedFreeItems) return [];

  if (maxAllowedFreeItems === 0) {
    return currentFreeItems.map((freeItem) =>
      storeRequester.removeItem(freeItem.cart_id, freeItem.id)
    );
  }

  return currentFreeItems.map((freeItem) => {
    const newQuantity = cross
      ? Math.floor(
          freeItem.quantity * (maxAllowedFreeItems / currentTotalFreeItems)
        )
      : calculateNonCrossNewQuantity(freeItem, remainingItems, N, M);

    return newQuantity > 0
      ? storeRequester.updateItem(freeItem.cart_id, freeItem.id, {
          quantity: newQuantity,
        })
      : storeRequester.removeItem(freeItem.cart_id, freeItem.id);
  });
}

function calculateNonCrossNewQuantity(freeItem, remainingItems, N, M) {
  const relevantRegularItem = remainingItems.find(
    (item) => !item.free && item.variant_id === freeItem.variant_id
  );
  if (!relevantRegularItem) return 0;

  const variantMaxFreeItems = Math.floor(relevantRegularItem.quantity / N) * M;
  return Math.min(freeItem.quantity, variantMaxFreeItems);
}
